/**
 * Advertorial page — countdown custom element.
 *
 * Two modes:
 *   deadline : counts down to a fixed date/time, then hides itself (honest).
 *   loop     : evergreen timer that restarts every N seconds. This is what the
 *              source PageFly page used. It is a marketing pattern, not a real
 *              deadline — some ad networks and consumer-protection rules treat
 *              it as misleading, so prefer "deadline" where you can.
 *
 * Custom element lifecycle handles Theme Editor re-renders: connectedCallback
 * starts the tick, disconnectedCallback clears it. State lives on the instance,
 * so several countdowns on one page never interfere.
 */
class AdvertorialCountdown extends HTMLElement {
  connectedCallback() {
    this.mode = this.getAttribute('data-mode') === 'deadline' ? 'deadline' : 'loop';
    this.period = Math.max(60, parseInt(this.getAttribute('data-period'), 10) || 7200);
    this.deadline = Date.parse(this.getAttribute('data-deadline') || '') || null;
    this.units = (this.getAttribute('data-units') || 'h,m,s').split(',').map((u) => u.trim()).filter(Boolean);
    this.labels = {
      d: this.getAttribute('data-label-d') || 'Days',
      h: this.getAttribute('data-label-h') || 'Hours',
      m: this.getAttribute('data-label-m') || 'Mins',
      s: this.getAttribute('data-label-s') || 'Secs',
    };
    this.render();
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  remaining() {
    if (this.mode === 'deadline') {
      if (!this.deadline) return 0;
      return Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
    }
    const now = Math.floor(Date.now() / 1000);
    return this.period - (now % this.period);
  }

  render() {
    this.cells = {};
    const frag = document.createDocumentFragment();
    this.units.forEach((u, i) => {
      if (i > 0) {
        const sep = document.createElement('span');
        sep.className = 'adv-count__sep';
        sep.textContent = ':';
        sep.setAttribute('aria-hidden', 'true');
        frag.appendChild(sep);
      }
      const wrap = document.createElement('span');
      wrap.className = 'adv-count__unit';
      const n = document.createElement('span');
      n.className = 'adv-count__n';
      n.textContent = '00';
      const l = document.createElement('span');
      l.className = 'adv-count__l';
      l.textContent = this.labels[u] || '';
      wrap.append(n, l);
      frag.appendChild(wrap);
      this.cells[u] = n;
    });
    this.replaceChildren(frag);
  }

  tick() {
    let s = this.remaining();
    if (this.mode === 'deadline' && s <= 0) {
      this.hidden = true;
      const owner = this.closest('[data-adv-countdown-group]');
      if (owner) owner.hidden = true;
      clearInterval(this.timer);
      return;
    }
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const vals = { d: d, h: this.units.includes('d') ? h : Math.floor(s / 3600), m: m, s: sec };
    const pad = (n) => String(n).padStart(2, '0');
    this.units.forEach((u) => {
      if (this.cells[u]) this.cells[u].textContent = pad(vals[u]);
    });
  }
}

if (!customElements.get('advertorial-countdown')) {
  customElements.define('advertorial-countdown', AdvertorialCountdown);
}
